import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Award, Users, Mic, Globe, BookOpen, Star, GraduationCap, Trophy, Heart, Tv, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DR_JASON_PHOTO = "https://customer-assets.emergentagent.com/job_edu-portal-276/artifacts/dbv2agov_IMG_3569%20%281%29.jpeg";

const GALLERY_PHOTOS = [
  { src: DR_JASON_PHOTO, caption: "Dr. Jason E. Williams, Ed.D." },
  { src: `${BACKEND_URL}/api/static/images/IMG_8219.JPG`, caption: "Graduation Day — National University" },
  { src: `${BACKEND_URL}/api/static/images/IMG_8220.JPG`, caption: "Commencement Ceremony" },
  { src: `${BACKEND_URL}/api/static/images/IMG_8221.JPG`, caption: "With Faculty at Graduation" },
];

export default function AboutJasonPage() {
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const [direction, setDirection] = useState(0);

  const nextPhoto = useCallback(() => {
    setDirection(1);
    setCurrentPhoto(prev => (prev + 1) % GALLERY_PHOTOS.length);
  }, []);

  const prevPhoto = useCallback(() => {
    setDirection(-1);
    setCurrentPhoto(prev => (prev - 1 + GALLERY_PHOTOS.length) % GALLERY_PHOTOS.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextPhoto, 5000);
    return () => clearInterval(timer);
  }, [nextPhoto]);

  const achievements = [
    { icon: GraduationCap, title: "Ed.D. Degree", description: "Doctor of Education in Organizational Innovation from National University" },
    { icon: Globe, title: "VR Pioneer", description: "Founder & CEO of The Matrix University 501(c)(3) nonprofit" },
    { icon: Trophy, title: "Star Athlete", description: "Former 400m hurdles record holder, gold medalist, CIF Champion" },
    { icon: BookOpen, title: "Award-Winning Author", description: "Two #1 Amazon bestselling children's books" },
    { icon: Tv, title: "Media Producer", description: "Writer & Executive Producer of 'Jason Visits the Museum' animated series" },
    { icon: Award, title: "Freedom of Speech Award", description: "First male recipient, presented by filmmaker Michael Moore" },
    { icon: Heart, title: "Mental Health Advocate", description: "Speaks globally about ADHD, special needs, and overcoming challenges" },
    { icon: Users, title: "Community Leader", description: "Former Director of Education, Riverside Black Chamber of Commerce" },
  ];

  return (
    <div className="min-h-screen pt-24" data-testid="about-jason-page">
      {/* Hero Section */}
      <section className="py-16">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Photo Carousel */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative group">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden glass-light relative">
                  <AnimatePresence mode="wait" initial={false} custom={direction}>
                    <motion.img
                      key={currentPhoto}
                      src={GALLERY_PHOTOS[currentPhoto].src}
                      alt={GALLERY_PHOTOS[currentPhoto].caption}
                      className="w-full h-full object-contain bg-black/40 absolute inset-0"
                      initial={{ opacity: 0, x: direction > 0 ? 80 : -80 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: direction > 0 ? -80 : 80 }}
                      transition={{ duration: 0.4 }}
                      data-testid="about-carousel-image"
                    />
                  </AnimatePresence>

                  {/* Caption overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                    <p className="text-white text-sm font-medium">{GALLERY_PHOTOS[currentPhoto].caption}</p>
                  </div>

                  {/* Navigation arrows */}
                  <button onClick={prevPhoto} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70" data-testid="carousel-prev">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button onClick={nextPhoto} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70" data-testid="carousel-next">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                {/* Dots */}
                <div className="flex justify-center gap-2 mt-4">
                  {GALLERY_PHOTOS.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => { setDirection(i > currentPhoto ? 1 : -1); setCurrentPhoto(i); }}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentPhoto ? 'bg-cta w-6' : 'bg-muted-foreground/40 hover:bg-muted-foreground'}`}
                      data-testid={`carousel-dot-${i}`}
                    />
                  ))}
                </div>

                <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-xl bg-primary/20 -z-10" />
                <div className="absolute -top-6 -left-6 w-32 h-32 rounded-xl bg-secondary/20 -z-10" />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4" data-testid="about-title">
                Dr. Jason E. Williams, Ed.D.
              </h1>
              <p className="text-xl text-secondary mb-6" data-testid="about-credential">
                Doctor of Education in Organizational Innovation
              </p>
              
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  <strong className="text-white">Dr. Jason E. Williams, Ed.D.</strong>, is a Doctor of Education (Ed.D.) specializing in Organizational Innovation. He holds a master's degree in education from National University, a bachelor's degree in communications with a minor in sociology from California State University San Marcos, and was a star scholar-athlete at Colorado State University.
                </p>
                <p>
                  Dr. Williams is an accomplished track and field athlete, having served as captain of the California State University San Marcos team and as a former 400-meter hurdles university record holder. He has won gold medals for the United States in Europe, CIF Champion, and placed 17th in the Junior Olympics at Walt Disney World. His international academic experience includes completing educational programs at the University of Pretoria in South Africa and participating in the Kemet Nu Tour of Egypt with renowned scholar Ashra Kwesi.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Extended Bio Section */}
      <section className="py-16 bg-card/50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-6 text-muted-foreground leading-relaxed">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-serif font-bold text-white mb-4">Educator & Coach</h2>
              <p>
                In addition to his athletic and academic pursuits, Dr. Williams has served as a substitute teacher, a high school basketball referee, and a high school track and field coach in Southern California, where his student-athletes became league champions, CIF champions, state champions, NCAA university champions, and recipients of full-ride athletic scholarships—some advancing to the professional level.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="text-2xl font-serif font-bold text-white mb-4">Overcoming Challenges & Advocacy</h2>
              <p>
                Dr. Williams has overcome several life hurdles, including an ADHD (Attention-Deficit/Hyperactivity Disorder) diagnosis in third grade. He now speaks, inspires, and teaches about his experiences with mental health disorders, students with special needs, mislabeling, and misdiagnosis to audiences around the world.
              </p>
              <p className="mt-4">
                While serving as captain of his university track and field team, he was the target of a high-profile racial-profiling incident at California State University San Marcos, which was covered by FOX News and other media outlets. He subsequently became the first male recipient of the Freedom of Speech Award Scholarship, presented by award-winning filmmaker Michael Moore.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-2xl font-serif font-bold text-white mb-4">Author & Media Producer</h2>
              <p>
                Dr. Jason E. Williams, Ed.D., is an award-winning children's book author; two of his three books have reached No. 1 on Amazon. Drawing on his creativity and innovation, he wrote and executive-produced the educational animated series <em className="text-secondary">Jason Visits the Museum</em>. He also served as director of education for the Riverside Black Chamber of Commerce, where he was recognized for his contributions, including co-hosting the first TEDx event held in Riverside, California.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="text-2xl font-serif font-bold text-white mb-4">The Matrix University</h2>
              <p>
                Dr. Williams is the founder and CEO of The Matrix University, a 501(c)(3) nonprofit dedicated to uplifting education, innovation, and entertainment. Currently, he is leading efforts in virtual reality education, having opened a Metaversity campus in partnership with leading U.S. VR companies to provide youth and adults with access to digital technology training for the 21st century, with a focus on at-promise, underrepresented, and Indigenous communities.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-16">
        <div className="container-custom">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-serif font-bold text-white text-center mb-12"
          >
            Achievements & Expertise
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {achievements.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-xl glass-light text-center card-hover"
                data-testid={`achievement-${index}`}
              >
                <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-card/50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6">
              Mission & Vision
            </h2>
            <div className="p-8 rounded-2xl glass-light">
              <Star className="w-12 h-12 text-cta mx-auto mb-6" />
              <p className="text-xl text-muted-foreground leading-relaxed">
                "My mission is to break down the barriers of traditional education and create immersive learning experiences that inspire curiosity, foster creativity, and connect people across cultures. Through The Matrix University, I envision a world where anyone, anywhere, can access transformative educational experiences that prepare them for the future—with a special focus on at-promise, underrepresented, and Indigenous communities."
              </p>
              <p className="text-white font-semibold mt-6">— Dr. Jason E. Williams, Ed.D.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center p-8 rounded-2xl glass-light"
          >
            <Mic className="w-12 h-12 text-secondary mx-auto mb-6" />
            <h2 className="text-2xl font-serif font-bold text-white mb-4">
              Book Dr. Williams
            </h2>
            <p className="text-muted-foreground mb-6">
              For events, media inquiries, merchandise, program information, sponsorships, donations, speaking engagements, and more, please feel free to contact Dr. Jason E. Williams, Ed.D.
            </p>
            <a href="/contact" className="btn-primary inline-flex items-center gap-2">
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
